import { Link, useSearchParams } from "react-router-dom";
import { useState } from "react";
import { List, Loader2, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import { n } from "../../lib/format";
import { SORTS } from "../../lib/constants";
import { locate, nearest } from "../../lib/geo";
import { SiteHeader } from "../SiteHeader";
import { ExploreMap } from "../LazyMaps";
import { prefetchProps } from "../../lib/prefetch";
import { Cover } from "../Illustration";
import { TopicBadge } from "../ProposalCards";
import { TopicLegend } from "../TopicLegend";
import { Empty } from "../Bits";
import { AreaChips, SearchForm, SortSeg, TopicSelect } from "../Filters";

const Toggle = ({ onClick }) => (
  <button type="button" onClick={onClick} className="btn btn-dark" data-testid="mobile-view-list-btn" style={{ borderRadius: 999, minHeight: 48, padding: "0 22px" }}>
    <List size={18} />List
  </button>
);

const MobileTile = ({ p, rank }) => (
  <li className="card-stretch" style={{ listStyle: "none", border: "1px solid var(--border-2)", borderRadius: 16, overflow: "hidden", background: "#fff" }} data-testid={`mobile-tile-${p.id}`}>
    <div style={{ position: "relative", aspectRatio: "16 / 9" }}>
      <Cover p={p} />
      <span aria-hidden="true" className="head" style={{ position: "absolute", top: 12, left: 12, width: 38, height: 38, borderRadius: 10, background: "#14232a", color: "#fff", fontSize: 20, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{rank}</span>
      <TopicBadge p={p} large style={{ left: 12, bottom: 12 }} testId={`mobile-tile-topic-${p.id}`} />
    </div>
    <div style={{ padding: 16 }}>
      <p className="meta" style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>{p.place}
        {p.trending && <span className="flag flag-trending">Trending</span>}{p.is_new && !p.trending && <span className="flag flag-new">New</span>}</p>
      <h3 className="head" style={{ fontSize: 20, lineHeight: 1.2, margin: "6px 0 10px" }}>
        <Link to={`/proposal/${p.slug}`} className="card-link" {...prefetchProps(p.slug)} data-testid={`mobile-tile-link-${p.id}`}><span className="sr-only">Number {rank}: </span>{p.title}</Link>
      </h3>
      <p style={{ margin: 0, fontSize: 14 }}><strong>{n(p.likes)}</strong> likes <span style={{ color: "var(--muted)" }}>· {p.pct}% positive</span></p>
    </div>
  </li>
);

const ListView = ({ s }) => (
  <main style={{ paddingBottom: 110 }} data-testid="mobile-list-view">
    <section aria-labelledby="mhero" style={{ background: "#f6eee8" }}>
      <div style={{ position: "relative", height: 256, overflow: "hidden" }}>
        <img src="/illustrations/cork-hero.svg" alt="" style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "70% bottom" }} />
      </div>
      <div style={{ padding: "18px 16px 22px" }}>
        <h1 id="mhero" className="head" style={{ fontWeight: 800, fontSize: 38, lineHeight: 1, letterSpacing: "-.03em", margin: "0 0 10px", color: "var(--ink)" }}>
          Your city. <span style={{ color: "#C8102E" }}>Your ideas.</span>
        </h1>
        <p style={{ fontSize: 17, lineHeight: 1.45, color: "#2f2f35", margin: 0 }}>Show how any corner of Cork could be better, and vote for the ideas you want to see happen.</p>
      </div>
    </section>
    <div style={{ padding: "20px 16px 0" }}>
      <h2 className="head" style={{ fontSize: 24, margin: "0 0 12px" }}>Explore ideas across Cork</h2>
      <SearchForm compact id="mq" value={s.draft} onChange={s.setDraft} onSubmit={() => s.setQ(s.draft.trim())} />
      <div style={{ marginTop: 14 }}><SortSeg sort={s.sort} onChange={s.setSort} mobile /></div>
    </div>
    <div style={{ padding: "14px 16px 0" }}><AreaChips area={s.area} onChange={s.setArea} scroll /></div>
    <div style={{ padding: "10px 16px 0" }}><TopicSelect topic={s.topic} onChange={s.setTopic} id="mobile-topic-filter" /></div>
    <p style={{ padding: "18px 16px 10px", margin: 0, fontWeight: 700 }} role="status">
      {SORTS.find((x) => x.key === s.sort).label} · {s.items.length} proposal{s.items.length === 1 ? "" : "s"}{s.topicName && ` in ${s.topicName}`}
    </p>
    <ol style={{ padding: "0 16px", margin: 0, display: "flex", flexDirection: "column", gap: 14 }}>
      {s.items.map((p, i) => <MobileTile key={p.id} p={p} rank={i + 1} />)}
    </ol>
    {!s.loading && !s.items.length && (
      <div style={{ padding: 16 }}>
        <Empty title="No proposals match">Try another area, topic or search. <button type="button" className="btn-link" onClick={s.clear} data-testid="mobile-clear-filters-btn">Clear filters</button></Empty>
      </div>
    )}
  </main>
);

const NearMe = ({ s }) => {
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const pos = await locate();
      const p = nearest(s.items, pos);
      s.nearMe(pos, p);
      if (!p) toast.info("No proposals match your filters yet.");
    } catch (e) {
      toast.error(e.code === 1 ? "Location is blocked for this site. Allow it in your browser settings and try again." : "We couldn’t find your location. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button type="button" className="near-me" onClick={go} disabled={busy} aria-label="Show the proposal nearest to me" data-testid="map-near-me">
      {busy ? <Loader2 size={18} className="spin" /> : <LocateFixed size={18} style={{ color: "var(--red)" }} />}{busy ? "Finding you…" : "Near me"}
    </button>
  );
};

const MAP_PADDING = { top: 120, bottom: 0, left: 0, right: 0 };

const MapView = ({ s, onList }) => {
  const count = s.items.length;
  return (
    <main style={{ position: "fixed", top: 56, left: 0, right: 0, bottom: "calc(var(--mobile-bar, 0px) + var(--mobile-bar-safe, 0px))", display: "flex", flexDirection: "column" }} data-testid="mobile-map-view">
      <div style={{ position: "relative", flex: 1, minHeight: 0 }}>
        <ExploreMap proposals={s.items} selectedId={s.selected} onSelect={s.setSelected} flyTo={s.fly} controls={false} zoom={12.4} padding={MAP_PADDING} me={s.me} />
        <NearMe s={s} />
        <div style={{ position: "absolute", top: 12, left: 12, right: 12, zIndex: 30, display: "flex", flexDirection: "column", gap: 10 }}>
          <div style={{ boxShadow: "0 4px 14px rgba(20,35,42,.18)", borderRadius: 14 }}>
            <SearchForm compact id="mapq" value={s.draft} onChange={s.setDraft} onSubmit={() => s.setQ(s.draft.trim())} />
          </div>
          <div className="chips-scroll">
            <div style={{ boxShadow: "0 2px 8px rgba(20,35,42,.15)", borderRadius: 999, flex: "none" }}>
              <TopicSelect topic={s.topic} onChange={s.setTopic} small id="map-topic-filter" />
            </div>
            {SORTS.map((x) => (
              <button key={x.key} type="button" className="chip chip-sm" aria-pressed={s.sort === x.key} onClick={() => s.setSort(x.key)}
                style={{ boxShadow: "0 2px 8px rgba(20,35,42,.15)" }} data-testid={`map-sort-${x.key}`}>{x.label}</button>
            ))}
          </div>
        </div>
      </div>
      <div style={{ flex: "none", background: "var(--soft)", borderTop: "1px solid var(--border)", paddingBottom: "var(--panel-safe, env(safe-area-inset-bottom))" }} data-testid="mobile-map-panel">
        <TopicLegend proposals={s.items} topic={s.topic} onTopic={s.setTopic} variant="row" testId="mobile-map-legend" />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, minHeight: 72, padding: "0 16px" }} data-testid="mobile-map-bar">
          <p className="meta" role="status" style={{ margin: 0 }} data-testid="mobile-map-status">
            {s.loading ? "Loading…" : `${count} proposal${count === 1 ? "" : "s"} · tap a pin to see it`}
          </p>
          <Toggle onClick={onList} />
        </div>
      </div>
    </main>
  );
};

export default function MobileHome({ s }) {
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "map" || s.focus ? "map" : "list";
  const setView = (v) => {
    const next = new URLSearchParams(params);
    if (v === "map") next.set("view", "map");
    else { next.delete("view"); next.delete("focus"); }
    setParams(next, { replace: true });
  };
  return (
    <div data-testid="home-page-mobile" style={{ position: "relative" }}>
      <SiteHeader transparent={view === "list"} />
      {view === "list"
        ? <ListView s={s} />
        : <MapView s={s} onList={() => setView("list")} />}
    </div>
  );
}
